/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */
import React, { useState, useEffect, type ComponentProps } from 'react';
import classnames from 'classnames';
import Cart from 'components/Icons/Cart/Cart';
import Client from './Client/Client';
import Time from './Time/Time';
import './Basket.scss';

const DEFAULT_ITEMS: BasketItem[] = [];

const DEFAULT_LABELS: BasketLabels = {
  tickets: {
    single: '{number} ticket',
    multiple: '{number} tickets',
  },
  merchandise: {
    single: '{number} merchandise',
    multiple: '{number} merchandise',
  },
  signin: 'Sign In',
  register: 'Register',
  total: 'Total',
  toBasket: 'To basket',
};

const DEFAULT_ON_EXPIRED = () => {};
const DEFAULT_ON_REFRESH = () => {};

interface BasketLabels {
  tickets: { single: string; multiple: string };
  merchandise: { single: string; multiple: string };
  signin: string;
  register: string;
  total: string;
  toBasket: string;
}

interface BasketItem {
  type: string;
  name: string;
  price: number;
  details?: string;
}

interface GroupedItem {
  type: 'tickets' | 'merchandise';
  name: string;
  number: number;
  price: number;
}

interface BasketProps {
  url: string;
  loginLink: string;
  registerLink: string;
  profileLink: string;
  client?:
    | (Omit<ComponentProps<typeof Client>, 'profileLink'> & {
        id?: string | number | null;
      })
    | null;
  expires?: number;
  items?: BasketItem[];
  labels?: BasketLabels;
  onExpired?: () => void;
  onRefresh?: () => void;
}

/**
 * This is the Basket component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
const Basket = ({
  loginLink,
  registerLink,
  profileLink,
  client = null,
  items = DEFAULT_ITEMS,
  expires = 0,
  url,
  labels = DEFAULT_LABELS,
  onExpired = DEFAULT_ON_EXPIRED,
  onRefresh = DEFAULT_ON_REFRESH,
}: BasketProps) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.classList.add('with-basket');

    return function cleanup() {
      document.body.classList.remove('with-basket');
    };
  }, []);

  const grouped: Record<string, GroupedItem> = {};

  const tickets = items.filter(item => item.type === 'Ticket');

  tickets.forEach(({ name, price, details }) => {
    const id = `ticket-${name}-${details}`;
    if (!grouped[id]) {
      grouped[id] = {
        type: 'tickets',
        name,
        number: 0,
        price: 0,
      };
    }
    grouped[id].number += 1;
    grouped[id].price += price;
  });

  const merchandise = items.filter(item => item.type === 'Product');

  merchandise.forEach(({ name, price, details }) => {
    const id = `merch-${name}-${details}`;
    if (!grouped[id]) {
      grouped[id] = {
        type: 'merchandise',
        name,
        number: 0,
        price: 0,
      };
    }
    grouped[id].number += 1;
    grouped[id].price += price;
  });

  const sumTotal = Object.keys(grouped).reduce((sum, key) => {
    return sum + grouped[key].price;
  }, 0);

  let timeout: ReturnType<typeof setTimeout> | undefined;

  function showItems() {
    clearTimeout(timeout);
    setOpen(true);
  }

  function hideItems() {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      setOpen(false);
    }, 200);
  }

  function renderNumberLabel(type: GroupedItem['type'], number: number) {
    if (type === 'tickets') {
      if (number < 2) {
        return labels.tickets.single.replace('{number}', String(number));
      }
      return labels.tickets.multiple.replace('{number}', String(number));
    }

    if (number < 2) {
      return labels.merchandise.single.replace('{number}', String(number));
    }
    return labels.merchandise.multiple.replace('{number}', String(number));
  }

  function renderClientLinks() {
    if (client && client.id) {
      return <Client {...client} profileLink={profileLink} />;
    }

    return [
      <a key="signin" href={loginLink}>
        {labels.signin}
      </a>,
      <a key="register" href={registerLink}>
        {labels.register}
      </a>,
    ];
  }

  return (
    <div
      className={classnames('basket', { 'show-items': open })}
      appear="true"
      root-margin="40px 0px 0px 0px"
      timeout="{2000}"
    >
      <div className="block-layout">
        <div className="top">
          <div className="outer-container">
            {renderClientLinks()}
            <Cart />
            <button
              className="shopping-basket"
              onMouseEnter={showItems}
              onMouseLeave={hideItems}
              onClick={() => {
                setOpen(visible => !visible);
              }}
            >
              <span className="count">
                {tickets.length + merchandise.length}
              </span>
            </button>
            <Time
              expires={expires}
              onRefresh={onRefresh}
              onExpired={onExpired}
            />
          </div>
        </div>
        <div className="outer-container details-container">
          <div
            className="details"
            onMouseEnter={showItems}
            onMouseLeave={hideItems}
          >
            <div>
              <div>
                <ul>
                  {Object.keys(grouped).map(key => (
                    <li key={key}>
                      <strong>{grouped[key].name}</strong>
                      <div>
                        <span>
                          {renderNumberLabel(
                            grouped[key].type,
                            grouped[key].number
                          )}
                        </span>
                        <span>
                          {grouped[key].price.toLocaleString('nb', {
                            currency: 'NOK',
                            style: 'currency',
                          })}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="total">
                  <span>{labels.total}</span>
                  <strong className="sum">
                    {sumTotal.toLocaleString('nb', {
                      currency: 'NOK',
                      style: 'currency',
                    })}
                  </strong>
                </div>

                <a
                  className="ticket-button"
                  href={`${url}?returnUrl=${encodeURIComponent(
                    window.location.href
                  )}`}
                >
                  <span>{labels.toBasket}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Basket;
